import React, {
  FC,
  useEffect,
  Suspense,
  ComponentProps,
  ReactNode,
  ComponentType,
} from "react";
import { ThemeProvider } from "@UI/Contexts/ThemeProvider";
import { I18nProvider } from "@I18n/Contexts/I18nProvider";
import { RouterProvider } from "@Libs/Router/Contexts/RouterContext";
import { DataLayerProvider } from "@Libs/DataLayer/Contexts/DataLayerContext";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { ExceptionMonitorProvider } from "@Libs/ExceptionMonitor/Contexts/ExceptionMonitorContext";
import { MetadataProvider } from "@Libs/Events/Contexts/MetadataContext";
import routes from "../config/routes";
import useCookies from "@Libs/Utils/hooks/useCookies";
import api from "@api";
import { IRoutes } from "@Libs/Router/types";

type EmptyObject = Record<never, never>;

interface IProviderProps {
  theme: EmptyObject;
  i18n: EmptyObject;
  exceptionMonitor: EmptyObject;
  suspense: {
    fallback: JSX.Element;
  };
  metadata: EmptyObject;
  router: {
    routes: IRoutes;
  };
  dataLayer: EmptyObject;
}

type IProviderName = keyof IProviderProps;

type IProvider<T extends IProviderName> = {
  Provider: FC<IProviderProps[T] & { children: ReactNode }>;
  name: T;
  props?: Record<string, unknown>;
};

const providers: IProvider<IProviderName>[] = [
  {
    Provider: I18nProvider,
    name: "i18n",
  },
  {
    Provider: ThemeProvider,
    name: "theme",
  },
  {
    Provider: RouterProvider,
    name: "router",
    props: {
      routes,
    },
  },
  {
    Provider: DataLayerProvider,
    name: "dataLayer",
  },
  {
    Provider: ExceptionMonitorProvider,
    name: "exceptionMonitor",
  },
  {
    Provider: Suspense,
    name: "suspense",
    props: {
      fallback: <LoadingScreen />,
    },
  },
  {
    Provider: MetadataProvider,
    name: "metadata",
  },
];

const compose = (
  ...providers: IProvider<IProviderName>[]
): FC<{ children: ReactNode }> => {
  return providers.reduce(
    (AccumulatedComponents, { Provider, props }) => {
      // eslint-disable-next-line react/display-name
      return ({ children }: ComponentProps<typeof Provider>): JSX.Element => {
        return (
          <AccumulatedComponents>
            <Provider {...props}>{children}</Provider>
          </AccumulatedComponents>
        );
      };
    },
    ({ children }) => <>{children}</>
  );
};

type IWithProviders = <T extends object>(
  C: ComponentType<T>,
  options?: { except?: IProviderName[] }
) => ComponentType<T>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withProviders: IWithProviders =
  (Component, options) =>
  // eslint-disable-next-line react/display-name
  (props) => {
    const cookies = useCookies();

    useEffect(() => {
      const token = cookies.get("authToken");

      if (token) {
        api.setAuthToken(token);
      }
    }, [cookies]);

    const providersToUse = providers.filter(
      (provider) => !options?.except || !options.except.includes(provider.name)
    );

    const ComposedProvider = compose(...providersToUse);

    return (
      <ComposedProvider>
        <Component {...props} />
      </ComposedProvider>
    );
  };

export default withProviders;
// eslint-enable react/display-name
